// src/components/search/RouteMap.js - Updated for new booking types

import React, { useEffect, useRef, useState } from 'react';
import { BOOKING_TYPES } from '../../utils/constants';

const RouteMap = ({ searchData, onRouteCalculated, className }) => {
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [directionsService, setDirectionsService] = useState(null);
  const [directionsRenderer, setDirectionsRenderer] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null);
  const lastRouteRef = useRef(null);

  // Check if this is a transfer type (shows route) or hourly type (shows only pickup)
  const isTransferType = searchData?.type === BOOKING_TYPES.AIRPORT_TRANSFER || 
                         searchData?.type === BOOKING_TYPES.CITY_POINT_TO_POINT ||
                         searchData?.subService === 'transfer'; // Fallback for compatibility

  const isHourlyType = searchData?.type === BOOKING_TYPES.AIRPORT_HOURLY || 
                       searchData?.type === BOOKING_TYPES.CITY_HOURLY ||
                       searchData?.subService === 'hourly'; // Fallback for compatibility

  // Initialize map
  useEffect(() => {
    if (!window.google || !mapRef.current) return;

    const mapInstance = new window.google.maps.Map(mapRef.current, {
      zoom: 10,
      center: { lat: 7.8731, lng: 80.7718 }, // Center of Sri Lanka
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }]
        }
      ]
    });

    setMap(mapInstance);
    setDirectionsService(new window.google.maps.DirectionsService());
    setDirectionsRenderer(new window.google.maps.DirectionsRenderer({
      map: mapInstance,
      suppressMarkers: false,
      polylineOptions: {
        strokeColor: '#2563eb',
        strokeWeight: 5,
        strokeOpacity: 0.8,
      },
    }));
  }, []);

  // Calculate and display route
  useEffect(() => {
    if (!map || !directionsService || !directionsRenderer || !searchData) return;

    const origin = {
      lat: searchData.fromLat,
      lng: searchData.fromLng,
    };

    // For transfer types, use the destination; for hourly, use origin
    const destination = isTransferType ? {
      lat: searchData.toLat,
      lng: searchData.toLng,
    } : origin;

    console.log('Booking Type:', searchData.type);
    console.log('Is Transfer Type:', isTransferType);
    console.log('Origin:', origin);
    console.log('Destination:', destination);


    if (isTransferType && destination.lat && destination.lng) {
      // Request directions for transfer services
      directionsService.route(
        {
          origin: origin,
          destination: destination,
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === 'OK') {
            directionsRenderer.setDirections(result);
            
            // Extract route information
            const route = result.routes[0];
            const leg = route.legs[0];
            
            const info = {
              distance: leg.distance.text,
              distanceValue: leg.distance.value, // in meters
              duration: leg.duration.text,
              durationValue: leg.duration.value, // in seconds
            };
            
            // Prevent redundant updates
            const infoStr = JSON.stringify(info);
            if (lastRouteRef.current !== infoStr) {
              lastRouteRef.current = infoStr;
              setRouteInfo(info);
              if (onRouteCalculated) {
                onRouteCalculated(info);
              }
            }
          } else {
            console.error('Directions request failed:', status);
            
            // Fallback: show both markers if directions fail
            const originMarker = new window.google.maps.Marker({
              position: origin,
              map: map,
              title: searchData.fromPlace,
              label: 'A',
            });
            
            const destMarker = new window.google.maps.Marker({
              position: destination,
              map: map,
              title: searchData.toPlace,
              label: 'B',
            });
            
            // Fit bounds to show both markers
            const bounds = new window.google.maps.LatLngBounds();
            bounds.extend(origin);
            bounds.extend(destination);
            map.fitBounds(bounds);

            // Still provide basic info
            const fallbackInfo = {
              distance: 'Route calculation failed',
              duration: 'Unable to calculate',
              distanceValue: 0,
              durationValue: 0,
            };
            
            const infoStr = JSON.stringify(fallbackInfo);
            if (lastRouteRef.current !== infoStr) {
              lastRouteRef.current = infoStr;
              setRouteInfo(fallbackInfo);
              if (onRouteCalculated) {
                onRouteCalculated(fallbackInfo);
              }
            }
          }
        }
      );
    }
 else if (isHourlyType) {
      // For hourly service, just show a marker
      map.setCenter(origin);
      map.setZoom(13);
      
      new window.google.maps.Marker({
        position: origin,
        map: map,
        title: searchData.fromPlace,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: '#2563eb',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        },
      });

      const serviceTypeLabel = searchData.type === BOOKING_TYPES.AIRPORT_HOURLY 
        ? 'Airport Hourly' 
        : 'City Hourly';

      const hourlyInfo = {
        distance: serviceTypeLabel,
        duration: `${searchData.hours} hours service`,
        distanceValue: 0,
        durationValue: searchData.hours * 3600,
      };
      
      setRouteInfo(hourlyInfo);
      
      // Pass hourly info back to parent
      if (onRouteCalculated) {
        onRouteCalculated(hourlyInfo);
      }
    } else {
      // Fallback for any edge cases
      map.setCenter(origin);
      map.setZoom(13);
      
      new window.google.maps.Marker({
        position: origin,
        map: map,
        title: searchData.fromPlace,
      });
    }
  }, [map, directionsService, directionsRenderer, searchData, onRouteCalculated, isTransferType, isHourlyType]);

  return (
    <div className={`${className || 'relative w-full h-64 md:h-80'} rounded-lg overflow-hidden border border-gray-200 shadow-sm`}>
      <div ref={mapRef} className="w-full h-full bg-gray-100" />
      
      {/* Route Info Overlay */}
      {routeInfo && (
        <div className="absolute top-4 left-4 bg-white rounded-lg shadow-lg p-3 max-w-xs">
          <div className="flex items-center space-x-3">
            {isTransferType ? (
              <>
                <div className="text-center">
                  <div className="text-xs text-gray-500">Distance</div>
                  <div className="font-semibold text-sm text-gray-900">{routeInfo.distance}</div>
                </div>
                <div className="w-px h-8 bg-gray-300"></div>
                <div className="text-center">
                  <div className="text-xs text-gray-500">Duration</div>
                  <div className="font-semibold text-sm text-gray-900">{routeInfo.duration}</div>
                </div>
              </>
            ) : (
              <div className="text-center">
                <div className="text-xs text-gray-500">Service Type</div>
                <div className="font-semibold text-sm text-gray-900">
                  {searchData.hours} hours
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* VAT Notice */}
      <div className="absolute bottom-4 left-4 bg-green-50 border border-green-200 rounded px-3 py-1">
        <div className="text-xs text-green-800 flex items-center">
          <span className="mr-1">✓</span>
          All prices include VAT, taxes & tolls
        </div>
      </div>
    </div>
  );
};

export default RouteMap;